import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { useToast } from '../../../../components/ui/Toast';
import { Decimal } from 'decimal.js';
import { useCreateWriteOff } from '../../../../features/finance/hooks';
import { SubscriberSearchInput, type SubscriberSearchResult } from '../../../../features/finance/components/SubscriberSearchInput';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/finance/write-offs/new')({
  component: ProvisionWriteOffPage,
});

const writeOffSchema = z.object({
  subscriberId: z.string().min(1, 'Subscriber is required'),
  ticketNumber: z.string().optional(),
  rupeesAmount: z.string().min(1, 'Amount is required'),
  writeOffDate: z.string().min(1, 'Date is required'),
  notes: z.string().max(500).optional(),
});

type WriteOffForm = z.infer<typeof writeOffSchema>;

const writeOffFormConfig = defineFormPage<WriteOffForm>({
  resource: 'write-offs',
  defaultValues: {
    subscriberId: '',
    ticketNumber: '',
    rupeesAmount: '',
    writeOffDate: new Date().toISOString().split('T')[0],
    notes: '',
  },
  schema: writeOffSchema,
});

function ProvisionWriteOffPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateWriteOff();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(writeOffFormConfig, createMutation, {
    routeId: 'finance/write-offs/new',
    onBeforeSubmit: (value) => {
      const rupees = new Decimal(value.rupeesAmount);
      if (rupees.isNaN() || rupees.lte(0)) throw new Error('Please enter a valid write-off amount.');
      return {
        subscriberId: value.subscriberId,
        ticketNumber: value.ticketNumber ? parseInt(value.ticketNumber, 10) : undefined,
        amountPaise: rupees.mul(100).round().toNumber(),
        writeOffDate: value.writeOffDate,
        notes: value.notes || undefined,
      } as never;
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Bad Debt Written Off',
        description: 'Account balance cleared from active ledger books and sent to Provision for Bad Debts asset logs.',
      });
      void navigate({ to: '/finance/write-offs' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Failed to Provision Write-off',
        description: err?.message || 'Could not record the write-off. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Record Write-Off"
      subtitle="Write off a bad/unrecoverable debt from the institutional books with full audit trace."
      backTo="/finance/write-offs"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="subscriberId"
              validators={{ onChange: ({ value }: { value: any }) => writeOffSchema.shape.subscriberId.safeParse(value).success ? undefined : writeOffSchema.shape.subscriberId.safeParse(value).error?.issues[0]?.message }}
              children={(field: any) => (
                <FormField label="Defaulting Subscriber" required>
                  <SubscriberSearchInput
                    value={field.state.value}
                    onChange={(subscriberId: string, subscriber?: SubscriberSearchResult) => {
                      field.handleChange(subscriberId);
                      if (subscriber && subscriber.ticketNumbers.length > 0) {
                        form.setFieldValue('ticketNumber' as never, String(subscriber.ticketNumbers[0]) as never);
                      }
                    }}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="ticketNumber"
              children={(field: any) => (
                <FormField label="Disputed Ticket">
                  <Input
                    placeholder="e.g. 18"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="rupeesAmount"
                validators={{ onChange: ({ value }: { value: any }) => writeOffSchema.shape.rupeesAmount.safeParse(value).success ? undefined : writeOffSchema.shape.rupeesAmount.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Principal Written-off (₹)" required>
                    <Input
                      type="number"
                      placeholder="e.g. 150000"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="writeOffDate"
                validators={{ onChange: ({ value }: { value: any }) => writeOffSchema.shape.writeOffDate.safeParse(value).success ? undefined : writeOffSchema.shape.writeOffDate.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Effective Action Date" required>
                    <Input
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            <form.Field
              name="notes"
              children={(field: any) => (
                <FormField label="Additional Notes">
                  <textarea
                    className="w-full h-24 p-3 border border-border rounded-lg bg-surface-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent-500"
                    placeholder="Enter write-off justification or tracking details..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/finance/write-offs' })} isSubmitting={isSubmitting} submitLabel="Provision Write-off" />
          </form>
    </FormPageLayout>
  );
}
